import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPinIcon, UserGroupIcon, TrophyIcon, ClockIcon, UserIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { it, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { updateDocument, db } from '../../services/firebase'
import { collection, addDoc } from 'firebase/firestore'
import { resolveNotificationMessage, matchTitleI18nParams } from '../../utils/notificationMessage'
import { getDerivedMatchStatus, getMatchRosterCapacity } from '../../utils/helpers'
import toast from 'react-hot-toast'

const MatchCard = ({ match, index, distance }) => {
  const { t, i18n } = useTranslation()
  const { user, userData } = useAuth()
  const locale = i18n.language === 'it' ? it : enUS
  const [imageError, setImageError] = useState(false)

  const getSportIcon = () => {
    return match.sport === 'calcio' ? '⚽' : '🎾'
  }

  const getSportDisplay = () => {
    const typeRaw = match?.matchType || ''
    const typeWithVs = typeRaw.replace(/v/gi, 'vs')
    const sportLabel = match?.sport === 'calcio' ? t('sports.calcio') : t('sports.padel')
    return `${getSportIcon()} ${typeWithVs} ${sportLabel}`.trim()
  }

  const getMatchTime = () => {
    if (!match?.date) return t('common.dateTbd')
    const date = new Date(match.date)
    if (Number.isNaN(date.getTime())) return t('common.dateTbd')
    return format(date, 'p', { locale })
  }

  const getMatchDate = () => {
    if (!match?.date) return t('common.dateTbd')
    const date = new Date(match.date)
    if (Number.isNaN(date.getTime())) return t('common.dateTbd')
    return format(date, 'dd/MM/yyyy', { locale })
  }

  const getWhere = () => {
    return match?.location || match?.address || t('common.locationTbd')
  }

  const getCity = () => {
    if (match?.city) return match.city
    const source = match?.address || match?.location || ''
    if (!source.includes(',')) return t('common.locationTbd')

    const parts = source
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)

    if (parts.length === 0) return t('common.locationTbd')

    const last = parts[parts.length - 1]
    if (/\d/.test(last) && parts.length > 1) {
      return parts[parts.length - 2]
    }

    return last
  }

  const getStatusBadge = () => {
    const derivedStatus = getDerivedMatchStatus(match?.date, match?.status)

    if (derivedStatus === 'cancelled') {
      return { className: 'bg-red-500/20 text-red-400 border border-red-500/30', text: t('matches.cancelled') }
    }

    switch(derivedStatus) {
      case 'upcoming':
        return { className: 'badge-success', text: t('matches.upcoming') }
      case 'ongoing':
        return { className: 'badge-primary', text: t('matches.inProgress') }
      case 'completed':
        return { className: 'bg-gray-500/20 text-gray-400 border border-gray-500/30', text: t('matches.completed') }
      default:
        return { className: 'badge-success', text: t('matches.upcoming') }
    }
  }

  const status = getStatusBadge()
  const derivedStatus = getDerivedMatchStatus(match?.date, match?.status)
  const totalRosterCapacity = getMatchRosterCapacity(match)
  const acceptedCount = match?.participants?.filter((participant) => participant.status === 'accepted').length ?? (match.currentPlayers || 0)
  const isFull = totalRosterCapacity > 0 && acceptedCount >= totalRosterCapacity
  const alreadyJoined = user ? match.participants?.some(p => p.userId === user.uid) : false
  const joinDisabled =
    derivedStatus === 'cancelled' || isFull || derivedStatus === 'completed' || alreadyJoined
  const joinUsesMutedStyle = alreadyJoined || isFull || derivedStatus === 'cancelled' || derivedStatus === 'completed'
  const joinButtonLabel = isFull && !alreadyJoined
    ? t('matchDetails.matchFull', { defaultValue: 'Match full' })
    : t('matchDetails.joinMatch')
  const joinJoinedClass =
    'cursor-not-allowed rounded-lg border border-gray-500/50 bg-gray-600/30 px-3 py-2.5 text-xs font-semibold text-gray-100 shadow-none sm:text-sm dark:border-gray-600 dark:bg-gray-800/85 dark:text-gray-300'
  const joinActiveMobileClass = `btn-gradient inline-flex flex-1 items-center justify-center whitespace-nowrap disabled:cursor-not-allowed ${alreadyJoined ? 'disabled:opacity-100' : 'disabled:opacity-50'}`
  const joinActiveDesktopClass = `btn-outline py-1.5 px-3 text-[11px] sm:text-xs whitespace-nowrap disabled:cursor-not-allowed ${alreadyJoined ? 'disabled:opacity-100' : 'disabled:opacity-50'}`
  const infoCardClass = 'rounded-lg border border-slate-200 bg-white/85 p-2.5 sm:p-2 dark:border-sport-silver/15 dark:bg-sport-darker/60'
  const infoCardDesktopClass = 'rounded-xl border border-slate-200 bg-white/85 p-3 dark:border-sport-silver/15 dark:bg-sport-darker/60'
  const infoLabelClass = 'flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500 dark:text-sport-silver/60'
  const infoValueClass = 'mt-1 text-xs text-slate-800 dark:text-sport-light break-words sm:truncate'

  const handleJoinMatch = async () => {
    if (!user || !userData) {
      toast.error(t('matchDetails.mustBeLoggedInToJoin'))
      return
    }

    if (alreadyJoined) {
      toast.error(t('matchDetails.alreadyInMatch'))
      return
    }

    if (isFull) {
      toast.error(t('matchDetails.rosterFull', { defaultValue: 'This match is full (all player and reserve spots are taken).' }))
      return
    }

    try {
      const isOrganizerJoining = match.organizerId === user.uid
      const updatedParticipants = [
        ...(match.participants || []),
        {
          userId: user.uid,
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          role: userData.role || '',
          status: isOrganizerJoining ? 'accepted' : 'pending',
          joinedAt: new Date().toISOString()
        }
      ]

      const updateData = {
        participants: updatedParticipants,
        updatedAt: new Date().toISOString()
      }

      if (isOrganizerJoining) {
        updateData.currentPlayers = (match.currentPlayers || 0) + 1
      }

      await updateDocument('matches', match.id, updateData)
      // Notify organizer when user requests to join
      if (!isOrganizerJoining && match.organizerId) {
        try {
          const senderName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || t('common.player')
          const titleParams = matchTitleI18nParams(match)
          await addDoc(collection(db, 'notifications'), {
            userId: match.organizerId,
            type: 'join_request',
            messageI18nKey: 'matchDetails.joinRequestNotification',
            messageI18nParams: { ...titleParams, senderName },
            message: resolveNotificationMessage(t, {
              messageI18nKey: 'matchDetails.joinRequestNotification',
              messageI18nParams: { ...titleParams, senderName },
              message: ''
            }),
            matchId: match.id,
            senderId: user.uid,
            senderName,
            read: false,
            createdAt: new Date().toISOString()
          })
        } catch (notifyErr) {
          console.error('Error creating join_request notification:', notifyErr)
        }
      }
      toast.success(isOrganizerJoining ? t('matchDetails.joinedMatch') : t('matchDetails.requestSent'))
    } catch (error) {
      console.error('Error joining match:', error)
      toast.error(`Error: ${error.code || error.message || 'Unknown error'}`)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="sport-card group"
    >
      <div className="p-3 md:hidden">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xl">{getSportIcon()}</span>
            <span className={`badge text-xs ${status.className}`}>{status.text}</span>
          </div>
        </div>

        <div className="mb-1">
          <h3 className="text-base font-bold text-sport-light font-['Orbitron'] line-clamp-2">
            {match.title || (match.sport === 'calcio' ? t('common.footballMatch') : t('common.padelMatch'))}
          </h3>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1.5 md:gap-2">
          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <UserIcon className="h-3 w-3 text-sport-primary" />
              <span>{t('matches.organizer')}</span>
            </div>
            <p className={infoValueClass}>{match.organizerName || '-'}</p>
          </div>

          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <TrophyIcon className="h-3 w-3 text-sport-secondary" />
              <span>{t('sports.sport')}</span>
            </div>
            <p className={infoValueClass}>{getSportDisplay()}</p>
          </div>

          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <MapPinIcon className="h-3 w-3 text-sport-accent" />
              <span>{t('auth.city')}</span>
            </div>
            <p className={infoValueClass}>{getCity()}</p>
          </div>

          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <CalendarIcon className="h-3 w-3 text-sport-primary" />
              <span>{t('matches.date', { defaultValue: 'Date' })}</span>
            </div>
            <p className={infoValueClass}>{getMatchDate()}</p>
          </div>

          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <ClockIcon className="h-3 w-3 text-sport-secondary" />
              <span>{t('matches.time')}</span>
            </div>
            <p className={infoValueClass}>{getMatchTime()}</p>
          </div>

          <div className={infoCardClass}>
            <div className={infoLabelClass}>
              <MapPinIcon className="h-3 w-3 text-sport-primary" />
              <span>{t('matches.location')}</span>
            </div>
            <p className={infoValueClass}>{getWhere()}</p>
          </div>

          {distance !== undefined && distance !== null && (
            <div className={infoCardClass}>
              <div className={infoLabelClass}>
                <MapPinIcon className="h-3 w-3 text-sport-secondary" />
                <span>{t('matches.distance')}</span>
              </div>
              <p className={infoValueClass}>{(distance).toFixed(1)} km</p>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2 min-[380px]:flex-row min-[380px]:items-center">
          <button
            type="button"
            onClick={handleJoinMatch}
            disabled={joinDisabled}
            title={isFull && !alreadyJoined ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
            className={
              joinUsesMutedStyle
                ? `${joinJoinedClass} inline-flex flex-1 items-center justify-center`
                : `${joinActiveMobileClass} px-3 py-2.5`
            }
          >
            {joinButtonLabel}
          </button>
          <Link
            to={`/match/${match.id}`}
            className="btn-outline inline-flex flex-1 items-center justify-center px-3 py-2.5 text-xs sm:text-sm whitespace-nowrap"
          >
            {t('matches.viewDetails')}
          </Link>
        </div>
      </div>

      <div className="hidden md:block">
        <div className="relative">
          {/* Left Side - Sport Icon + Join Button */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <span className="text-3xl drop-shadow-lg">{getSportIcon()}</span>
            <button
              type="button"
              onClick={handleJoinMatch}
              disabled={joinDisabled}
              title={isFull && !alreadyJoined ? t('matchDetails.matchFull', { defaultValue: 'Match full' }) : undefined}
              className={
                joinUsesMutedStyle
                  ? `${joinJoinedClass} py-1.5 px-3 text-[11px] sm:text-xs`
                  : joinActiveDesktopClass
              }
            >
              {joinButtonLabel}
            </button>
          </div>

          {/* Right Side - Status Badge */}
          <div className="absolute top-4 right-4 z-10">
            <span className={`badge ${status.className}`}>
              {status.text}
            </span>
          </div>

          {/* Cover Image */}
          <div className="h-48 bg-gradient-to-br from-sport-primary/20 to-sport-secondary/20 relative overflow-hidden rounded-t-2xl">
            <div className="absolute inset-0 bg-sport-dark/60"></div>
            {match.coverImage && !imageError ? (
              <img 
                src={match.coverImage} 
                alt={match.title} 
                className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <TrophyIcon className="h-20 w-20 text-sport-silver/20" />
              </div>
            )}

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-sport-dark via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-xl font-bold text-sport-light mb-3 font-['Orbitron'] group-hover:text-sport-primary transition-colors">
            {match.title || (match.sport === 'calcio' ? t('common.footballMatch') : t('common.padelMatch'))}
          </h3>

          <div className="space-y-3 mb-5">
            <div className="grid grid-cols-2 gap-2">
              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <UserIcon className="h-3 w-3 text-sport-primary" />
                  <span>{t('matches.organizer')}</span>
                </div>
                <p className={infoValueClass}>{match.organizerName || '-'}</p>
              </div>

              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <TrophyIcon className="h-3 w-3 text-sport-secondary" />
                  <span>{t('sports.sport')}</span>
                </div>
                <p className={infoValueClass}>{getSportDisplay()}</p>
              </div>

              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <MapPinIcon className="h-3 w-3 text-sport-accent" />
                  <span>{t('auth.city')}</span>
                </div>
                <p className={infoValueClass}>{getCity()}</p>
              </div>

              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <CalendarIcon className="h-3 w-3 text-sport-primary" />
                  <span>{t('matches.date', { defaultValue: 'Date' })}</span>
                </div>
                <p className={infoValueClass}>{getMatchDate()}</p>
              </div>

              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <ClockIcon className="h-3 w-3 text-sport-secondary" />
                  <span>{t('matches.time')}</span>
                </div>
                <p className={infoValueClass}>{getMatchTime()}</p>
              </div>

              <div className={infoCardDesktopClass}>
                <div className={infoLabelClass}>
                  <MapPinIcon className="h-3 w-3 text-sport-primary" />
                  <span>{t('matches.location')}</span>
                </div>
                <p className={infoValueClass}>{getWhere()}</p>
              </div>

              {distance !== undefined && distance !== null && (
                <div className={infoCardDesktopClass}>
                  <div className={infoLabelClass}>
                    <MapPinIcon className="h-3 w-3 text-sport-secondary" />
                    <span>{t('matches.distance')}</span>
                  </div>
                  <p className={infoValueClass}>{(distance).toFixed(1)} km</p>
                </div>
              )}
            </div>

            <div className="flex items-center text-sport-silver/70 rounded-xl border border-sport-silver/10 bg-sport-darker/40 p-3">
              <div className="p-2 rounded-lg bg-sport-darker mr-3">
                <UserGroupIcon className="h-4 w-4 text-sport-accent" />
              </div>
              <span className={`text-sm ${isFull ? 'text-red-400 font-semibold' : ''}`}>
                {acceptedCount}/{totalRosterCapacity} {t('matches.players')}
                {isFull && ` • ${t('common.full')}`}
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-sport-silver/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex flex-row gap-2 sm:justify-end">
              <Link
                to={`/match/${match.id}`}
                className="btn-gradient py-2 px-4 text-sm whitespace-nowrap"
              >
                {t('matches.viewDetails')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default MatchCard
